import { useCallback, useState } from 'react';
import { useAuthStore, loginUrlForSlug } from '@clarix/auth-store';
import { getApiClient } from '@clarix/api-client';
import { clearPinnedSidebarPanel } from '../context/SidebarContext';

/**
 * Where sign-out lands: `/login` on the SAME origin, carrying the workspace.
 *
 * A workspace subdomain stays on its subdomain, so `{slug}.clarix.sarvadhi.work`
 * signs out to `{slug}.clarix.sarvadhi.work/login` and LoginRoute reads the slug
 * from the hostname. Signing out no longer hops to the apex, which would drop
 * the slug and force the user back through the workspace picker just to return
 * to the workspace they were already in.
 *
 * SINGLE-HOST deployments (local dev, tunnel) have no per-workspace origin — the
 * slug lives in the session, and signing out clears it. So it is handed over in
 * the URL instead (`/login?workspace=acme`, see `loginUrlForSlug`) and the shell
 * adopts it back at boot. Without that, local sign-out always lands on "which
 * workspace?" even though the answer never changed.
 *
 * Same-origin means no `?force-logout=1` hint is needed: localStorage is
 * per-origin and this origin's copy was just cleared in-process, so there is no
 * stale token for the destination to hydrate.
 */
/**
 * Shared sign-out flow — best-effort server revoke, local token wipe, then a
 * hard navigation back to the shell login. Consumed by both the UserMenu
 * dropdown and the platform profile page so the logout behavior stays in one
 * place.
 */
export function useSignOut() {
  const clear = useAuthStore((s) => s.clear);
  const [signingOut, setSigningOut] = useState(false);

  const signOut = useCallback(async () => {
    if (signingOut) return;
    setSigningOut(true);
    // Read the slug BEFORE clear() wipes it — it is the only copy on a
    // single-host deployment, and it has to survive into the redirect.
    const slug = useAuthStore.getState().workspaceSlug;
    // Best-effort server-side revoke. Network/401 failures are non-fatal —
    // we still clear the local token and bounce the user off the app.
    try {
      await getApiClient().post('/auth/logout');
    } catch {
      /* ignore */
    }
    clear();
    // Belt-and-suspenders: also nuke the persisted entry on THIS origin in
    // case zustand's persist middleware flushes a stale value during unload.
    try {
      localStorage.removeItem('clarix.auth.v1');
    } catch {
      /* ignore quota / disabled storage */
    }
    // Session chrome doesn't outlive the session: forget the pinned sidebar
    // panel so the next login (possibly a different user) starts clean.
    clearPinnedSidebarPanel();
    // Hard navigation: drops in-memory app state on the way out. Same-origin,
    // so a workspace subdomain keeps its slug in the host; single-host keeps it
    // in the query.
    window.location.replace(loginUrlForSlug(slug));
  }, [clear, signingOut]);

  return { signOut, signingOut };
}
